import { useState } from "react";
import { Page } from "../components/AppShell";
import { BarList } from "../components/charts";
import {
  Banner,
  Card,
  PanelState,
  RangeFilter,
  StatTile,
  Swatch,
} from "../components/ui";
import { num, pct, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { ColorsResponse } from "../lib/types";

/**
 * ── The page a paint company actually came here for ────────────────────────
 * Which shades are being chosen, in what family, at what finish.
 *
 * The three counts are shown side by side and never added together. They mean
 * different things: a RENDER is someone spending a model call to see a shade
 * on their own wall, a DRAFT is a shade currently sitting on a project, and a
 * FAVOURITE is a tap in the fandeck. A shade that is favourited constantly and
 * rendered rarely is a shade people like the look of and do not commit to —
 * a merchandising fact that a single "popularity" number would erase.
 */
export function ColorsPage() {
  const [days, setDays] = useState(90);
  const { data, error, loading, stale } = useApi<ColorsResponse>(
    "/api/admin/analytics/colors",
    { days, limit: 20 },
  );

  const totalRenders =
    data?.finishes.reduce((n, f) => n + f.renders, 0) ?? 0;

  return (
    <Page
      title="Colours"
      subtitle="What the fandeck is actually being used for"
      actions={<RangeFilter days={days} onChange={setDays} />}
    >
      <div className={stale ? "stale" : undefined}>
        <PanelState loading={loading} error={error}>
          {data && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {data.truncated && (
                <Banner tone="warning" title="Showing a recent sample">
                  This window holds more renders than the aggregator reads in
                  one pass, so these counts describe the most recent 25,000
                  rather than all of them. Narrow the range for exact numbers.
                </Banner>
              )}

              <div className="grid grid-kpi">
                <StatTile
                  label="Shades used"
                  value={data.colors.length >= 20 ? "20+" : data.colors.length}
                  hint="in the top list below"
                />
                <StatTile
                  label="Families in play"
                  value={data.families.length}
                  hint="of 12"
                />
                <StatTile
                  label="Renders analysed"
                  value={data.sampledRenders}
                  hint={`over ${days} days`}
                />
                <StatTile
                  label="Top family"
                  value={data.families[0]?.label ?? "—"}
                  hint={
                    data.families[0]
                      ? `${num(data.families[0].renders)} renders`
                      : undefined
                  }
                />
              </div>

              <div className="grid grid-2">
                <Card
                  title="Most-chosen shades"
                  note="Ranked by renders — the signal that cost something. Drafts and favourites are shown beside it, never added to it."
                  className="span-2"
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.colors.length === 0}
                    emptyText="No shades were used in this period."
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: 34 }}>
                              <span className="sr-only">Swatch</span>
                            </th>
                            <th>Shade</th>
                            <th>Code</th>
                            <th>Family</th>
                            <th className="num">Rendered</th>
                            <th className="num">By people</th>
                            <th className="num">On projects</th>
                            <th className="num">Hearted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.colors.map((c) => (
                            <tr key={c.code ?? c.hex}>
                              <td>
                                <Swatch hex={c.hex} title={`${c.name} ${c.hex}`} />
                              </td>
                              <td className="cell-strong">{c.name}</td>
                              <td className="mono cell-mute">
                                {c.code ?? c.hex}
                              </td>
                              <td className="cell-mute">
                                {data.families.find((f) => f.key === c.family)
                                  ?.label ?? titleCase(c.family)}
                              </td>
                              <td className="num cell-strong">
                                {num(c.renders)}
                              </td>
                              <td className="num">{num(c.renderUsers)}</td>
                              <td className="num">{num(c.drafts)}</td>
                              <td className="num">{num(c.favorites)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </PanelState>
                </Card>

                <Card
                  title="Colour families"
                  note="Where the demand sits across the range. The swatch is that family's most-rendered shade; the bar stays one colour so a pale family is still readable."
                >
                  <PanelState
                    loading={false}
                    error={null}
                    empty={data.families.length === 0}
                  >
                    <BarList
                      items={data.families.map((f) => ({
                        key: f.key,
                        label: f.label,
                        value: f.renders,
                        swatch: f.swatch || undefined,
                        meta: `${f.shades} shade${f.shades === 1 ? "" : "s"}`,
                      }))}
                    />
                  </PanelState>
                </Card>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 14 }}
                >
                  <Card
                    title="Finish mix"
                    note="This one maps straight onto a product SKU."
                  >
                    <PanelState
                      loading={false}
                      error={null}
                      empty={data.finishes.length === 0}
                    >
                      <BarList
                        items={data.finishes.map((f) => ({
                          key: f.id,
                          label: titleCase(f.id),
                          value: f.renders,
                          meta:
                            totalRenders > 0
                              ? pct((f.renders / totalRenders) * 100, 0)
                              : undefined,
                        }))}
                      />
                    </PanelState>
                  </Card>

                  <Card
                    title="Lighting chosen"
                    note="Which light people picture their room in."
                  >
                    <PanelState
                      loading={false}
                      error={null}
                      empty={data.lighting.length === 0}
                    >
                      <BarList
                        items={data.lighting.map((l) => ({
                          key: l.id,
                          label: titleCase(l.id),
                          value: l.renders,
                        }))}
                      />
                    </PanelState>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </PanelState>
      </div>
    </Page>
  );
}
